/*
 * @Descripttion: 首次登录弹窗组件 - 引导绑定服务号
 * @Author: qhx
 * @Date: 2026-05-08
 * @LastEditTime: 2026-05-08
 */
import React, { useEffect, useState, useRef } from "react";
import { message, Modal } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { serviceCheckLogin } from "../../../../api/login/index.js";
import refreshImg from "../../assets/refresh.png";
import wxBg from '@/assets/images/login/wx-bg.png';
import successImg from '@/assets/images/login/success.svg';
import iconImg from '@/assets/images/login/icon.svg';
import "./index.scss";

interface Props {
  visible: boolean;
  onClose: () => void;
  timestamp?: number;
  type: string;
}

const FirstLoginModal: React.FC<Props> = ({ visible, onClose, timestamp, type }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const clSetTime = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const [qrcodeInvalid, setQrcodeInvalid] = useState<boolean>(false);
  const [currentTimestamp, setCurrentTimestamp] = useState<number>(
    timestamp || new Date().getTime()
  );

  // 二维码 URL 生成
  useEffect(() => {
    if (visible) {
      setQrCodeUrl(
        `/uc/wx/getServiceQrCode.json?scene=${currentTimestamp}`
      );
    }
  }, [currentTimestamp, visible]);

  // 重置失效状态
  useEffect(() => {
    if (visible) {
      setQrcodeInvalid(false);
    }
  }, [visible]);

  // 轮询检查登录状态
  useEffect(() => {
    clearTimeAll();
    if (!qrCodeUrl || !visible) {
      return;
    }
    let isMounted = true;

    const handleXcxCheckLogin = async (): Promise<void> => {
      clearTimeAll();
      controllerRef.current = new AbortController();
      const result: any = await serviceCheckLogin(currentTimestamp, controllerRef.current.signal);
      const res = result.data;
      
      if (isMounted && res.success) {
        message.success("绑定成功，即将前往量子云");
        // 绑定成功，延迟1.5秒后通知父组件并跳转
        setTimeout(() => {
          onClose();
        }, 1500);
        return;
      }
      // 轮询
      if (res.code === 10071 && isMounted) {
        clearTimeAll();
        clSetTime.current = setTimeout(() => {
          handleXcxCheckLogin();
        }, 2000);
      } else if (res.code === 10070) {
        // 二维码失效
        setQrcodeInvalid(true);
        clearTimeAll();
      } else if (res.code === 10075 && isMounted) {
        setCurrentTimestamp(new Date().getTime());
        message.error('抱歉，当前微信已被其他量子云账号绑定，请更换微信扫码重试');
      }else {
        message.error(res.message);
        clearTimeAll();
      }
    };

    const imgtest: HTMLImageElement = new Image();
    imgtest.src = qrCodeUrl;
    imgtest.onload = function () {
      handleXcxCheckLogin();
    };

    return () => {
      isMounted = false;
      clearTimeAll();
      controllerRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCodeUrl, visible]);
  

  const clearTimeAll = () => {
    if (clSetTime.current) {
      clearTimeout(clSetTime.current);
      clSetTime.current = null;
    }
  };

  // 刷新二维码
  const handleRefreshQRcode = (): void => {
    setCurrentTimestamp(new Date().getTime());
    setQrcodeInvalid(false);
  };

  // 跳过绑定
  const handleSkip = (): void => {
    onClose();
  };

  return (
    <Modal
      open={visible}
      onCancel={handleSkip}
      footer={null}
      width={800}
      className="first-login-modal-wrapper"
      centered
      maskClosable={false}
    >
      <div className="first-login-modal-content">
        <div className="modal-header">
          <div className="modal-title">
            <img src={successImg} alt="success" />
            <span>{type === 'login' ? '登录成功' : '注册成功'}</span>
          </div>
        </div>
        
        <div className="modal-desc">{`${type === 'login' ? '您是首次登录，':''}推荐绑定服务号，操作更灵活`}</div>
        
        <div className="modal-qrcode-box">
          <div className="qrcode-tips">
            使用微信扫描下方二维码"本源量子云社区"服务号二维码，完成关注后即可自动绑定，后续可使用微信扫码一键登录到当前账户，方便快捷，同时还能解锁更多量子资源哦。
          </div>
          
          <div className="qrcode-content-wrapper">
            <div className="qrcode-left">
              <div className="wx-login-box">
                <img src={wxBg} alt="" className="wx-bg-img" />
                <img className="qrcode-img" src={qrCodeUrl} alt="服务号二维码" />
                <div
                  onClick={handleRefreshQRcode}
                  className={`refresh-err ${qrcodeInvalid ? "active" : ""}`}
                >
                  <img src={refreshImg} alt="刷新" />
                  <div>二维码失效</div>
                  <div>请点击重试</div>
                </div>
              </div>
              <p className="qrcode-hint">（使用微信扫一扫 关注后立即绑定）</p>
            </div>
            
            <div className="qrcode-right">
              <div className="benefit-item">
                <div className="benefit-item-title">
                  <img src={iconImg} alt='' className="benefit-icon-img" />
                  <span className="benefit-title">更快捷的登录</span>
                </div>
                <div className="benefit-desc">后续可通过微信扫码直接登录，无需接收验证码和记住密码</div>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-item-title">
                  <img src={iconImg} alt='' className="benefit-icon-img" />
                  <span className="benefit-title">更系统的访问体验</span>
                </div>
                <div className="benefit-desc">公众号提供常见的量子云需求分类，通过手机即可访问使用</div>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-item-title">
                  <img src={iconImg} alt='' className="benefit-icon-img" />
                  <span className="benefit-title">更全面的信息</span>
                </div>
                <div className="benefit-desc">公众号定期会发布量子计算内容干货和信息，一定不要错过</div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="skip-btn" onClick={handleSkip}>暂不绑定，登录并返回</button>
        </div>
      </div>
    </Modal>
  );
};

export default FirstLoginModal;